// Cuando hay cuentas duplicadas (el mismo cliente entrado dos veces, o dos
// personas que comparten teléfono), un `docs[0]` agarra una al azar: el
// cliente entraba y le salían los datos y las reservaciones de la otra
// cuenta (reporte del cliente 2026-09-10). Estas funciones escogen SIEMPRE
// la misma — y la correcta.

const lower = (v) => String(v || '').trim().toLowerCase();
const digits = (v) => String(v || '').replace(/\D/g, '');

const toMillis = (v) => {
  if (!v) return 0;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (v.seconds) return v.seconds * 1000;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
};

// Mientras más alto, mejor candidato
const scoreMember = (m, { email, phone }) => {
  let score = 0;
  const wantEmail = lower(email);
  const wantPhone = digits(phone);
  const hasEmail = lower(m.email);
  const hasPhone = digits(m.phone);

  // Lo que más pesa: que coincidan las DOS cosas — es la misma persona
  if (wantEmail && wantPhone && hasEmail === wantEmail && hasPhone === wantPhone) score += 100;
  if (wantEmail && hasEmail === wantEmail) score += 20;
  if (wantPhone && hasPhone === wantPhone) score += 20;
  // Un miembro activo es la cuenta "de verdad"; los duplicados suelen quedar inactivos
  if (m.active === true) score += 10;
  // Récord más completo antes que una cáscara vacía
  if (lower(m.fullName)) score += 2;
  if (hasEmail) score += 1;
  if (hasPhone) score += 1;
  return score;
};

// docs: array de { id, ...data } (ya mapeados) o de QueryDocumentSnapshot
export const pickBestMember = (docs, criteria = {}) => {
  // _ref va como propiedad NO enumerable: así el objeto se puede guardar en
  // localStorage con JSON.stringify (una DocumentReference de Firestore es
  // circular y lo reventaría) pero el código que necesita escribir en el doc
  // sigue teniendo `._ref`.
  const list = (docs || []).map((d) => {
    if (typeof d.data !== 'function') return d;
    const obj = { id: d.id, ...d.data() };
    Object.defineProperty(obj, '_ref', { value: d.ref, enumerable: false });
    return obj;
  });
  if (list.length === 0) return null;
  if (list.length === 1) return list[0];
  return list.slice().sort((a, b) => {
    const diff = scoreMember(b, criteria) - scoreMember(a, criteria);
    if (diff !== 0) return diff;
    // Empate: la más usada recientemente
    return toMillis(b.updatedAt || b.createdAt) - toMillis(a.updatedAt || a.createdAt);
  })[0];
};

// true si dos récords son creíblemente la MISMA persona. Se usa antes de
// escribir encima de un récord existente: dos personas pueden compartir un
// teléfono (matrimonios, papá e hijo) y no se le puede pisar el nombre y la
// licencia a la otra.
export const isSamePerson = (existing, incoming) => {
  const eEmail = lower(existing.email);
  const iEmail = lower(incoming.email);
  if (eEmail && iEmail) return eEmail === iEmail;
  // Si a alguno le falta el email, se compara por nombre (sin acentos)
  const eName = lower(existing.fullName).normalize('NFD').replace(/[̀-ͯ]/g, '');
  const iName = lower(incoming.fullName).normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (!eName || !iName) return true;
  return eName === iName;
};

// Agrupa duplicados para avisarle al staff en el listado de usuarios
export const findDuplicateKeys = (members = []) => {
  const byEmail = new Map();
  const byPhone = new Map();
  members.forEach((m) => {
    const e = lower(m.email);
    const p = digits(m.phone);
    if (e) byEmail.set(e, (byEmail.get(e) || 0) + 1);
    if (p.length >= 7) byPhone.set(p, (byPhone.get(p) || 0) + 1);
  });
  return {
    emails: new Set([...byEmail].filter(([, n]) => n > 1).map(([k]) => k)),
    phones: new Set([...byPhone].filter(([, n]) => n > 1).map(([k]) => k)),
  };
};

export const memberDuplicateReason = (member, dupes) => {
  if (!dupes) return null;
  const e = lower(member.email);
  const p = digits(member.phone);
  if (e && dupes.emails.has(e)) return 'Hay otra cuenta con este mismo email';
  if (p.length >= 7 && dupes.phones.has(p)) return 'Hay otra cuenta con este mismo teléfono';
  return null;
};
